import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { useLang } from '../i18n/context';
import './AirdropPage.css';
import '../styles/pro-page.css';

function AirdropPage() {
  const navigate = useNavigate();
  const [airdrops, setAirdrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAirdrop, setSelectedAirdrop] = useState(null);
  const { t, lang } = useLang();

  useEffect(() => {
    const fetchAirdrops = async () => {
      try {
        const { data, error } = await supabase.from('airdrops').select('*');
        if (error) throw error;
        const now = new Date();
        const normalized = (Array.isArray(data) ? data : []).map(a => {
          let autoStatus = a.status;
          if (a.end_time && new Date(a.end_time) <= now) autoStatus = 'ended';
          else if (a.start_time && new Date(a.start_time) > now) autoStatus = 'upcoming';
          else if (a.start_time && new Date(a.start_time) <= now) autoStatus = 'ongoing';
          return { ...a, status: autoStatus };
        });
        setAirdrops(normalized);
      } catch {
        setAirdrops([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAirdrops();
  }, []);

  const statusOrder = { upcoming: 0, ongoing: 1, ended: 2 };
  const filteredAirdrops = airdrops
    .filter(a => statusFilter === 'all' || a.status === statusFilter)
    .sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);
  const ongoingCount = airdrops.filter(a => a.status === 'ongoing').length;
  const upcomingCount = airdrops.filter(a => a.status === 'upcoming').length;
  const endedCount = airdrops.filter(a => a.status === 'ended').length;

  const getStatusText = (status) => ({
    upcoming: t('airdrop.statusUpcoming'),
    ongoing: t('airdrop.statusOngoing'),
    ended: t('airdrop.statusEnded'),
  }[status] || '');

  const filterTabs = [
    { key: 'all', label: t('airdrop.filterAll') },
    { key: 'ongoing', label: t('airdrop.filterOngoing') },
    { key: 'upcoming', label: t('airdrop.filterUpcoming') },
    { key: 'ended', label: t('airdrop.filterEnded') },
  ];

  const formatDate = (d) => {
    if (!d) return t('airdrop.tbd');
    return new Date(d).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <main className="pro-page">
      <div className="pro-page-inner">
        <button type="button" className="pro-back" onClick={() => navigate('/')}>{t('airdrop.backHome')}</button>
        <header className="pro-hero">
          <p className="pro-hero-label">{t('airdrop.eyebrow')}</p>
          <h1>{t('airdrop.title')}</h1>
          <span className="pro-hero-desc">{t('airdrop.desc')}</span>
        </header>

      <section className="ap-content">
        <div className="ap-toolbar">
          <div className="ap-filters">
          {filterTabs.map(tab => (
            <button
              key={tab.key}
              className={`ap-filter-btn${statusFilter === tab.key ? ' active' : ''}`}
              onClick={() => setStatusFilter(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="ap-mini-stats">
          <span className="ap-mini-stat ap-mini-ongoing"><strong>{ongoingCount}</strong> {t('airdrop.statusOngoing')}</span>
          <span className="ap-mini-stat ap-mini-upcoming"><strong>{upcomingCount}</strong> {t('airdrop.statusUpcoming')}</span>
          <span className="ap-mini-stat ap-mini-ended"><strong>{endedCount}</strong> {t('airdrop.statusEnded')}</span>
        </div>
        </div>
        {loading ? (
          <div className="ap-empty">{t('airdrop.loading')}</div>
        ) : filteredAirdrops.length === 0 ? (
          <div className="ap-empty">{t('airdrop.noData')}</div>
        ) : (
          <div className="ap-grid">
            {filteredAirdrops.map(airdrop => (
              <div
                className={`ap-card ap-card-${airdrop.status}`}
                key={airdrop.id}
                onClick={() => setSelectedAirdrop(airdrop)}
              >
                <div className="ap-card-header">
                  <h3>{airdrop.name}</h3>
                  <span className={`ap-status ap-status-${airdrop.status}`}>
                    {getStatusText(airdrop.status)}
                  </span>
                </div>
                <div className="ap-card-info">
                  {airdrop.reward && (
                    <div><span className="ap-card-label">{t('airdrop.rewardLabel')}</span>{airdrop.reward}</div>
                  )}
                  {airdrop.end_time && (
                    <div><span className="ap-card-label">{t('airdrop.deadlineLabel')}</span>{airdrop.end_time}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 详情弹窗 */}
      {selectedAirdrop && (
        <div className="ap-modal-overlay" onClick={() => setSelectedAirdrop(null)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <button className="ap-modal-close" onClick={() => setSelectedAirdrop(null)}>
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>

            <div className="ap-modal-status-row">
              <span className={`ap-status ap-status-${selectedAirdrop.status}`}>
                {getStatusText(selectedAirdrop.status)}
              </span>
            </div>

            <h2 className="ap-modal-title">{selectedAirdrop.name}</h2>

            {selectedAirdrop.reward && (
              <div className="ap-modal-field">
                <span className="ap-modal-label">{t('airdrop.rewardLabel')}</span>
                <span className="ap-modal-value ap-modal-reward">{selectedAirdrop.reward}</span>
              </div>
            )}

            <div className="ap-modal-times">
              <div className="ap-modal-field">
                <span className="ap-modal-label">{t('airdrop.startTime')}</span>
                <span>{formatDate(selectedAirdrop.start_time)}</span>
              </div>
              <div className="ap-modal-field">
                <span className="ap-modal-label">{t('airdrop.endTime')}</span>
                <span>{formatDate(selectedAirdrop.end_time)}</span>
              </div>
            </div>

            {selectedAirdrop.description && (
              <div className="ap-modal-desc">
                <div className="ap-modal-label">{t('airdrop.descLabel')}</div>
                <div className="ap-modal-desc-text">{selectedAirdrop.description}</div>
              </div>
            )}

            {selectedAirdrop.link && (
              <a
                className="ap-modal-link"
                href={selectedAirdrop.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('airdrop.viewDetail')} →
              </a>
            )}
          </div>
        </div>
      )}
    </div>
    </main>
  );
}

export default AirdropPage;
